"use client";

/**
 * Right side sheet for filling Ashby application (screening) answers before
 * sync/push (#20). Entry points: sync confirm modal (optional apply-to-all)
 * and candidate profile (no apply-to-all).
 *
 * Field defs + existing answers load from promotion-precheck when `fields`
 * is omitted. Tests may pass `fields` to skip the fetch.
 *
 * SELECT / MULTI_SELECT use a text-input fallback (options not on defs yet).
 */

import * as React from "react";
import {
  Sheet,
  SheetHeader,
  SheetTitle,
  SheetClose,
  SheetBody,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button, SubmitButton } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Icon } from "@/components/ui/icon";
import { showToast } from "@/components/ui/sonner";
import {
  useGetAtsPromotionPrecheck,
  usePatchAtsApplicationAnswers,
} from "@/lib/api/generated";
import type { ApplicationAnswersPatchFields } from "@/lib/api/model";
import { ApiError } from "@/lib/api/error";
import {
  draftFromAnswers,
  fromApiFieldDefs,
  toSheetFields,
  type ApplicationFieldDef,
  type DraftAnswer,
} from "../../lib/application-field-def";

export interface ApplicationAnswersSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  jobCandidateId: string;
  /**
   * Optional override for field defs (tests). When omitted, loads from
   * promotion-precheck `required_application_fields`.
   */
  fields?: ApplicationFieldDef[];
  /** Existing answers override. When omitted, uses precheck `application_answers`. */
  initialAnswers?: Record<string, unknown> | null;
  candidateName?: string;
  /**
   * Other hard-blocked (fillable) job_candidate_ids in the current sync
   * selection. When provided, shows Apply to all blocked. Omit on profile.
   */
  blockedJobCandidateIds?: string[];
  /** Called after a successful PATCH (sync modal refreshes preflight here). */
  onSaved?: () => void;
}

/** TEXT fallback covers SELECT / MULTI_SELECT until options land on defs. */
function controlForType(field: ApplicationFieldDef): "text" | "number" | "boolean" {
  const t = field.type.toUpperCase();
  if (t === "BOOLEAN") return "boolean";
  if (t === "NUMBER") return "number";
  return "text";
}

export function ApplicationAnswersSheet({
  open,
  onOpenChange,
  jobId,
  jobCandidateId,
  fields: fieldsProp,
  initialAnswers,
  candidateName,
  blockedJobCandidateIds,
  onSaved,
}: ApplicationAnswersSheetProps) {
  const loadFromPrecheck = fieldsProp === undefined;
  const precheck = useGetAtsPromotionPrecheck(jobId, jobCandidateId, {
    query: {
      enabled: open && loadFromPrecheck && !!jobId && !!jobCandidateId,
    },
  });

  const fields = React.useMemo(() => {
    if (fieldsProp !== undefined) return toSheetFields(fieldsProp);
    return toSheetFields(
      fromApiFieldDefs(precheck.data?.required_application_fields),
    );
  }, [fieldsProp, precheck.data?.required_application_fields]);

  const answersBlob = React.useMemo(() => {
    if (initialAnswers !== undefined) return initialAnswers;
    const raw = precheck.data?.application_answers;
    return raw && typeof raw === "object"
      ? (raw as Record<string, unknown>)
      : null;
  }, [initialAnswers, precheck.data?.application_answers]);

  const defsLoading = loadFromPrecheck && precheck.isLoading;
  const defsError = loadFromPrecheck && precheck.isError;

  const showApplyToAll =
    Array.isArray(blockedJobCandidateIds) &&
    blockedJobCandidateIds.some((id) => id !== jobCandidateId);

  const [draft, setDraft] = React.useState<Record<string, DraftAnswer>>({});
  const [applyToAll, setApplyToAll] = React.useState(false);
  const [seededFor, setSeededFor] = React.useState<string | null>(null);

  const seedKey = `${jobCandidateId}:${fields.map((f) => f.id).join(",")}:${open}:${defsLoading}`;
  if (open && !defsLoading && seededFor !== seedKey) {
    setSeededFor(seedKey);
    setDraft(draftFromAnswers(fields, answersBlob));
    setApplyToAll(false);
  }
  if (!open && seededFor !== null) {
    setSeededFor(null);
  }

  const patchMut = usePatchAtsApplicationAnswers();

  function setField(id: string, patch: Partial<DraftAnswer>) {
    setDraft((prev) => ({
      ...prev,
      [id]: { ...(prev[id] ?? { value: "", blank: false }), ...patch },
    }));
  }

  const canSave = fields.every((f) => {
    const d = draft[f.id];
    if (!d) return false;
    if (d.blank) return f.blankable;
    if (controlForType(f) === "boolean") {
      return d.value === "true" || d.value === "false";
    }
    return d.value.trim().length > 0;
  });

  async function handleSave() {
    if (!canSave || patchMut.isPending) return;

    let applyIds =
      applyToAll && blockedJobCandidateIds
        ? blockedJobCandidateIds.filter((id) => id !== jobCandidateId)
        : [];

    if (applyIds.length > 0) {
      const ok = window.confirm(
        `Apply these answers to ${applyIds.length} other blocked ${
          applyIds.length === 1 ? "profile" : "profiles"
        }? This may overwrite answers already filled on those profiles.`,
      );
      if (!ok) {
        // Cancel apply-to-all — still save the current candidate.
        setApplyToAll(false);
        applyIds = [];
      }
    }

    const patchFields: ApplicationAnswersPatchFields = {};
    for (const f of fields) {
      const d = draft[f.id] ?? { value: "", blank: false };
      if (d.blank && f.blankable) {
        patchFields[f.id] = { value: null, blank: true, source: "screening" };
      } else {
        let value: unknown = d.value;
        if (controlForType(f) === "boolean") {
          value = d.value === "true";
        } else if (controlForType(f) === "number" && d.value.trim() !== "") {
          value = Number(d.value);
        }
        patchFields[f.id] = { value, blank: false, source: "screening" };
      }
    }

    try {
      await patchMut.mutateAsync({
        jobId,
        jobCandidateId,
        data: {
          fields: patchFields,
          apply_to_job_candidate_ids:
            applyIds.length > 0 ? applyIds : undefined,
        },
      });
      showToast.success("Application answers saved");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message || "Couldn't save application answers"
          : "Couldn't save application answers";
      showToast.error(message);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange} size="md">
      <SheetHeader>
        <div className="flex items-center gap-1.5 min-w-0">
          <Icon name="IconFileText" className="size-4 shrink-0" />
          <SheetTitle className="truncate">Application answers</SheetTitle>
        </div>
        <SheetClose />
      </SheetHeader>
      <SheetBody className="gap-4">
        {candidateName ? (
          <p className="text-sm text-muted-foreground">
            For <span className="font-medium text-foreground">{candidateName}</span>
          </p>
        ) : null}

        {defsLoading ? (
          <p className="text-sm text-muted-foreground">Loading questions…</p>
        ) : defsError ? (
          <p className="text-sm text-destructive">
            Couldn&apos;t load application questions. Try again.
          </p>
        ) : fields.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No fillable application questions for this job.
          </p>
        ) : (
          <div className="flex flex-col gap-4">
            {fields.map((field) => {
              const d = draft[field.id] ?? { value: "", blank: false };
              const kind = controlForType(field);
              const disabled = d.blank && field.blankable;
              return (
                <div key={field.id} className="flex flex-col gap-2">
                  <Label htmlFor={`app-answer-${field.id}`}>
                    {field.label || field.id}
                    {field.required ? (
                      <span className="text-destructive"> *</span>
                    ) : null}
                  </Label>
                  {kind === "boolean" ? (
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={d.value === "true"}
                          disabled={disabled}
                          onChange={(checked) =>
                            setField(field.id, {
                              value: checked ? "true" : "",
                              blank: false,
                            })
                          }
                          aria-label={`${field.label} yes`}
                        />
                        Yes
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={d.value === "false"}
                          disabled={disabled}
                          onChange={(checked) =>
                            setField(field.id, {
                              value: checked ? "false" : "",
                              blank: false,
                            })
                          }
                          aria-label={`${field.label} no`}
                        />
                        No
                      </label>
                    </div>
                  ) : (
                    <Input
                      id={`app-answer-${field.id}`}
                      variant="modal"
                      type={kind === "number" ? "number" : "text"}
                      value={d.value}
                      disabled={disabled}
                      onChange={(e) =>
                        setField(field.id, {
                          value: e.target.value,
                          blank: false,
                        })
                      }
                      placeholder={field.label || "Answer"}
                    />
                  )}
                  {field.blankable ? (
                    <label className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Checkbox
                        checked={d.blank}
                        onChange={(checked) =>
                          setField(field.id, {
                            blank: checked,
                            value: checked ? "" : d.value,
                          })
                        }
                        aria-label={`Leave ${field.label} blank`}
                      />
                      Leave blank
                    </label>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}

        {showApplyToAll ? (
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={applyToAll}
              onChange={setApplyToAll}
              aria-label="Apply to all blocked"
            />
            Apply to all blocked profiles in this sync
          </label>
        ) : null}
      </SheetBody>
      <SheetFooter>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => onOpenChange(false)}
          disabled={patchMut.isPending}
        >
          Cancel
        </Button>
        <SubmitButton
          type="button"
          variant="primary"
          size="sm"
          disabled={!canSave || fields.length === 0 || defsLoading}
          loading={patchMut.isPending}
          onClick={() => void handleSave()}
        >
          Save
        </SubmitButton>
      </SheetFooter>
    </Sheet>
  );
}
